/**
 *
 * Communications
 *
 */

import React, { memo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import {
  flushStateCommunications,
  setInitialID,
  // list
  loadCommunications,
  // item
  loadCommunication,
  changeCommunication,
  hardDeleteCommunication,
  copyCommunication,
  // answers
  loadCommunicationAnswers,
  changeCommunicationAnswers,
  // update active
  updateActiveElement,
} from "./actions";

import {
  makeSelectInitialID,
  makeSelectFirstRender,
  makeSelectItemsListFirstElem,
  // list
  makeSelectItemsList,
  selectCommunicationsLoading,
  selectCommunicationsError,
  makeSelectItemsListFilteringParams,
  makeSelectItemsListNoMore,
  makeSelectItemsListTotalAmount,
  makeSelectItemsListPages,
  makeSelectListActiveElem,
  // new
  makeSelectIsNew,
  makeSelectIsNewSuccessful,
  // update item
  makeSelectUpdateSuccessful,
  // item
  makeSelectLoadingUpdate,
  selectCommunicationDetail,
  makeSelectHardDeleteLoading,
  makeSelectHardDeleteSuccessful,
  makeSelectLoadDetail,
  // answers load
  makeSelectLoadAnswers,
  makeSelectAnswersLoading,
  makeSelectAnswersError,
  // answers  update
  makeSelectPlan,
  makeSelectPlanLoading,
  makeSelectPlanSuccess,
  makeSelectPlanError,
  // copy
  makeSelectCopyLoading,
  makeSelectCopySuccessful,
} from "./selectors";

// antd component
import { Layout, Form } from "antd";
const { Content } = Layout;

//components
import DynamicSideList from "components/DynamicSideList";
import Preloader from "components/Preloader";
import {
  NotificationHandleNew,
  NotificationHandleUpdate,
  NotificationSimple,
} from "components/Notifications";
// inner components
import { CommunicationDetail } from "./components";
// init forms
import { initFormsCommunication } from "utils/forms/forms.init";
// helper for forms
import { setFormValues } from "utils/forms/forms.utils";
import { useRefState } from "utils/hooks";
import { generateAudienceIncludes } from "utils/helpers/helper.generateData";
import { getObjId } from "utils/helpers/helper.general";
import { parseQuery, encodeQueryData } from "utils/history";
// contexts
import { withTheme } from "engine/Contexts/Theme.context";

export function Communications({
  // main data
  itemsList,
  initialID,
  activeElement,
  // item
  loadDetail,
  loadingUpdate,
  updateSuccessful,
  isNew,
  isNewSuccessful,
  itemDetail,
  // answers load
  answers,
  loadAnswers,
  errorAnswers,
  // answers update
  planData,
  updatePlan,
  updatePlanSuccessful,
  errorPlan,
  // states
  firstRender,
  history,
  // copy
  copyLoading,
  copySuccessful,
  copyElement,
  // delete
  hardDeleteLoading,
  hardDeleteSuccessful,
  // list
  error,
  loading,
  params,
  noMore,
  totalAmount,
  pages,
  // actions
  loadList,
  loadCommunication,
  changeCommunication,
  hardDelete,
  flushState,
  setInitialID,
  updateActiveElement,
  // answers
  loadCommunicationAnswers,
  changeCommunicationAnswers,
  // contexts
  theme,
  //default
  className,
}) {
  useInjectReducer({ key: "communications", reducer });
  useInjectSaga({ key: "communications", saga });

  const { t, i18n } = useTranslation();

  const newKey = "new";
  const location = useLocation();
  const searchParams = parseQuery(location.search) || {};
  const { id: initId } = useParams();

  // Form initialization for "general" tab
  const [customForm] = Form.useForm();

  const [currentParams, currentParamsRef, setCurrentParams] = useRefState(
    searchParams || {},
  );
  const [generalForm, generalFormRef, setGeneralForm] = useRefState([]);

  const [currentElement, currentElementRef, setCurrentElement] = useRefState(
    itemDetail || null,
  );

  const getInitForm = useCallback(() => initFormsCommunication(), [i18n?.language]);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (!itemsList || itemsList?.length <= 0) {
      const initParams = {
        ...params,
        ...searchParams,
      };
      setCurrentParams(initParams);
      loadList(initParams, true, pages);

      const { page, ...searchData } = initParams;
      setSearchParams(searchData);
    }

    if (initId) {
      setInitialID(initId);
      initId != newKey && loadCommunication(initId);
      initId != newKey && loadCommunicationAnswers(initId);
      // open new form
      if (initId == newKey) {
        const initForm = getInitForm();
        !!initForm && setGeneralForm(initForm);
      }
    }

    return () => {
      // Anything in here is fired on component unmount.
      // Clearing the state after unmounting a component
      flushState();
    };
  }, []);

  useEffect(() => {
    setInitialID(initId);
  }, [initId]);

  // Get first element from lists
  // Because history not trigger update component
  useEffect(() => {
    if (initialID) {
      setCurrentElement(false);
      if (initialID && initialID !== newKey) {
        loadCommunication(initialID);
        loadCommunicationAnswers(initialID);
      }
    }
  }, [initialID]);

  useEffect(() => {
    if (!loadDetail && itemDetail) {
      setCurrentElement(itemDetail);

      const listEl = itemsList?.find(
        el => getObjId(el) == getObjId(itemDetail),
      );
      if (!!listEl) {
        updateActiveElement(listEl);
      } else if (itemDetail?._id) {
        updateActiveElement(itemDetail);
      }

      const itemObj = {
        ...itemDetail,
        "description-editor": itemDetail?.description || "",
      };
      const includes = generateAudienceIncludes(itemObj);
      const initForm = getInitForm(itemObj, includes);
      !!initForm && setGeneralForm(initForm);
    } else if (!loadDetail && !itemDetail) {
      resetForm();
      const initData = {
        audience: [],
        "audience-select": [],
        description: "",
        "description-editor": "",
        thumbnail: "",
      };
      const initForm = getInitForm(initData);
      !!initForm && setGeneralForm(initForm);
      customForm.setFieldsValue(initData);
    }
  }, [loadDetail, itemDetail]);

  // set form values from DB after form fileds is generated
  useEffect(() => {
    if (generalForm && generalForm.length > 0 && itemDetail) {
      const itemObj = {
        ...itemDetail,
        "description-editor": itemDetail?.description || "",
      };
      // reset data in the general form
      setFormData(itemObj);
    }
  }, [generalForm, itemDetail]);

  const setSearchParams = useCallback(
    data => {
      const search = parseQuery(location.search) || {};
      let currentData = {
        ...search,
        ...data,
      };
      const { page, ...searchData } = currentData;
      const serachStr = encodeQueryData(searchData);
      history.push({
        search: `?${serachStr}`,
        state: {
          params: currentData,
        },
      });
    },
    [history, location.search],
  );

  const changeCurrentItem = useCallback(
    (currentId, element) => {
      setInitialID(currentId);
      element?._id && updateActiveElement(element);
      const search = parseQuery(location.search) || {};
      let currentData = search;
      const serachStr = encodeQueryData(currentData);
      history.push({
        pathname: `/communications/${currentId}`,
        search: `?${serachStr}`,
        hash: "",
        state: {
          preview: false,
          params: currentData,
        },
      });
    },
    [history, location.search],
  );
  const addNewItem = useCallback(() => {
    setInitialID(newKey);
    setCurrentElement(false);
    updateActiveElement(false);
    const search = parseQuery(location.search) || {};
    let currentData = search;
    const serachStr = encodeQueryData(currentData);
    history.push({
      pathname: `/communications/${newKey}`,
      search: `?${serachStr}`,
      state: {
        preview: false,
        params: currentData,
      },
    });
    resetForm();
  }, [history, newKey, location.search]);

  // Change filter params and load new item for infinite scroll
  const changeFilterParams = useCallback(
    newParams => {
      if (
        newParams &&
        Object.keys(newParams).length > 0 &&
        currentParamsRef?.current &&
        Object.keys(currentParamsRef?.current).length > 0 &&
        JSON.stringify(currentParamsRef?.current) != JSON.stringify(newParams)
      ) {
        const newParamObj = {
          ...currentParamsRef?.current,
          ...newParams,
        };
        const { page, ...searchData } = newParamObj;
        setSearchParams(searchData);
        setCurrentParams(newParamObj);
        loadList(newParamObj, false, pages);
      } else if (
        Object.keys(currentParamsRef?.current).length == 0 &&
        newParams &&
        Object.keys(newParams).length > 0
      ) {
        const newParamObj = { ...params, ...newParams };
        const { page, ...searchData } = newParamObj;
        setCurrentParams(newParamObj);
        setSearchParams(searchData);
        loadList(newParamObj, false, pages);
      }
    },
    [params, currentParams, currentParamsRef, pages],
  );

  // Appointments Plan Actions
  const onUpdatePlanHandle = useCallback(
    (planArray, actionType = "save") => {
      const id = initId || currentElement?._id || initialID;
      changeCommunicationAnswers(planArray, actionType, id);
    },
    [currentElement?._id, initId, initialID],
  );

  /**
   * Form init actions
   */
  const resetForm = () => customForm.resetFields();
  const submitForm = () => customForm.submit();
  const setFormFieldsValues = values => {
    values && customForm.setFieldsValue(values);
  };
  // update data in the general form
  const setFormData = useCallback(
    (formData, genForm = null) => {
      const formValues = formData || currentElement;
      const mainForm = genForm || generalFormRef?.current;
      !!formValues && setFormValues(customForm, mainForm, formValues);
    },
    [generalFormRef, currentElement, customForm],
  );

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.communications")}</title>
        <meta name="description" content="Description of Communications" />
      </Helmet>

      <NotificationHandleUpdate
        Loading={loadingUpdate}
        isNew={isNew}
        Successful={updateSuccessful}
      />
      <NotificationHandleNew
        Loading={loadingUpdate}
        isNew={isNew}
        Successful={isNewSuccessful}
      />
      <NotificationSimple
        Loading={copyLoading}
        Successful={copySuccessful}
        message={`${t("common.duplicate")}!`}
        messageSuccess={`${t("common.duplicated")}!`}
        description={t("notifications.duplicateSuccess")}
      />
      <NotificationSimple
        Loading={hardDeleteLoading}
        Successful={hardDeleteSuccessful}
      />

      {loading && !!!itemsList && <Preloader />}

      <DynamicSideList
        className={!!theme?.hasValueModalOnSaveChanges ? "preventClass" : ""}
        type="communications"
        title={t("labels.communications")}
        list={itemsList}
        loadingList={loading}
        changeParams={changeFilterParams}
        firstRender={firstRender}
        loadParams={{
          ...currentParams,
          ...params,
          ...searchParams,
        }}
        totalAmount={totalAmount}
        noMore={noMore}
        initialID={initialID}
        activeElement={activeElement}
        currentItemAction={changeCurrentItem}
        addAction={addNewItem}
      />

      <Content className="main-content-container">
        {!!itemsList && (
          <CommunicationDetail
            // main form
            form={{
              customForm: customForm,
              resetForm: resetForm,
              submitForm: submitForm,
              setFormData: setFormData,
              setFormFieldsValues: setFormFieldsValues,
            }}
            formFields={generalForm || generalFormRef?.current}
            answers={answers}
            // data
            initialID={initialID}
            setInitID={setInitialID}
            currentElement={currentElement}
            setCurrentElement={setCurrentElement}
            //actions
            update={changeCommunication}
            copy={copyElement}
            hardDelete={hardDelete}
            updateActiveElement={elementData => {
              const listEl = itemsList?.find(
                el => getObjId(el) == getObjId(currentElement),
              );
              if (!!listEl) {
                const newEl = {
                  ...currentElement,
                  ...elementData,
                };
                setCurrentElement(prev => ({ ...prev, ...elementData }));
                updateActiveElement(newEl);
              } else {
                if (!!elementData) {
                  updateActiveElement({
                    ...currentElement,
                  });
                }
              }
            }}
            // loading
            loading={loadDetail}
            loadingList={loading}
            loadAnswers={loadAnswers}
            // appointments plan
            onUpdatePlan={onUpdatePlanHandle}
            loadPlan={updatePlan}
            loadPlanSuccess={updatePlanSuccessful}
            mode={
              currentElement?.status == "sent"
                ? "onlyRead"
                : currentElement?._id
                ? "edit"
                : "new"
            }
          />
        )}
      </Content>
    </Layout>
  );
}

Communications.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadingUpdate: PropTypes.bool,
  hardDeleteSuccessful: PropTypes.bool,
  loadList: PropTypes.func.isRequired,
  updateElement: PropTypes.func,
  hardDelete: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  // list
  initialID: makeSelectInitialID(),
  firstElem: makeSelectItemsListFirstElem(),
  activeElement: makeSelectListActiveElem(),
  firstRender: makeSelectFirstRender(),
  itemsList: makeSelectItemsList(),
  loading: selectCommunicationsLoading(),
  error: selectCommunicationsError(),
  params: makeSelectItemsListFilteringParams(),
  noMore: makeSelectItemsListNoMore(),
  totalAmount: makeSelectItemsListTotalAmount(),
  pages: makeSelectItemsListPages(),
  // item
  loadingUpdate: makeSelectLoadingUpdate(),
  updateSuccessful: makeSelectUpdateSuccessful(),
  isNew: makeSelectIsNew(),
  isNewSuccessful: makeSelectIsNewSuccessful(),
  itemDetail: selectCommunicationDetail(),
  loadDetail: makeSelectLoadDetail(),
  // answers load
  answers: makeSelectLoadAnswers(),
  loadAnswers: makeSelectAnswersLoading(),
  errorAnswers: makeSelectAnswersError(),
  // answers update
  planData: makeSelectPlan(),
  updatePlan: makeSelectPlanLoading(),
  updatePlanSuccessful: makeSelectPlanSuccess(),
  errorPlan: makeSelectPlanError(),
  // delete
  hardDeleteLoading: makeSelectHardDeleteLoading(),
  hardDeleteSuccessful: makeSelectHardDeleteSuccessful(),
  // copy
  copyLoading: makeSelectCopyLoading(),
  copySuccessful: makeSelectCopySuccessful(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateCommunications()),
    loadList: (params, firstRenderValue) =>
      dispatch(loadCommunications(params, firstRenderValue)),
    loadCommunication: detailID => dispatch(loadCommunication(detailID)),
    loadCommunicationAnswers: detailID =>
      dispatch(loadCommunicationAnswers(detailID)),
    changeCommunicationAnswers: (array, action, commID, type) =>
      dispatch(changeCommunicationAnswers(array, action, commID, type)),
    changeCommunication: itemData => dispatch(changeCommunication(itemData)),
    hardDelete: hardDeleteId => dispatch(hardDeleteCommunication(hardDeleteId)),
    copyElement: toCopyID => dispatch(copyCommunication(toCopyID)),
    setInitialID: initialID => dispatch(setInitialID(initialID)),
    updateActiveElement: activeEl => dispatch(updateActiveElement(activeEl)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
  withTheme,
)(Communications);
